import DataLayerService from "../../DataLayerService.js";
import DlPageview from "../../components/dl-pageview.js";
import { getMandantId, getBaseMandantId, getBaseMandantName } from "../basedata/mandant.js";
import getDeviceSize from "../basedata/device-size.js";
import getViewportWidth from "../basedata/viewport-width.js";
import { getStage } from "../basedata/stage.js";
import { getReferrer } from "../basedata/referrer.js";
import { getColorScheme } from "../basedata/color-scheme.js";
import { getKundennummer } from "../basedata/kundennummer.js";

/**
 * Handler für das Sammeln und Pushen von page_view Events
 *
 * Sammelt beim window.load Event alle dl-pageview Elemente auf der Seite,
 * kombiniert deren Daten mit Basedata und pusht ein einziges page_view Event.
 */
export default class PageviewHandler {
    #datalayerService;
    #initialized = false;

    /**
     * Erstellt einen neuen PageviewHandler
     * @param {DataLayerService} datalayerService - DataLayerService Instanz
     */
    constructor(datalayerService = new DataLayerService()) {
        this.#datalayerService = datalayerService;
    }

    /**
     * Initialisiert den Handler und registriert den window.load Event Listener
     */
    init() {
        if (this.#initialized) {
            console.warn('[PageviewHandler] Already initialized');
            return;
        }

        this.#initialized = true;

        // Warte auf window.load Event
        if (document.readyState === 'complete') {
            // Seite ist bereits geladen - verwende setTimeout damit alle
            // dl-pageview Elemente erst vollständig erstellt werden
            setTimeout(() => {
                this.#collectAndPushPageView();
            }, 0);
        } else {
            // Registriere Event Listener für load Event
            window.addEventListener('load', () => {
                this.#collectAndPushPageView();
            });
        }
    }

    /**
     * Sammelt alle dl-pageview Elemente und pusht ein kombiniertes page_view Event
     * @private
     */
    #collectAndPushPageView() {
        // Sammle Basedata
        const basedata = this.#getBasedata();

        // Sammle alle Parameter von allen dl-pageview Elementen
        const elementParams = {};
        const elements = document.querySelectorAll(DlPageview.tag);

        elements.forEach(element => {
            if (element instanceof DlPageview) {
                const params = element.params;

                // Pushe jedes dl-pageview Element einzeln als Datenobjekt
                try {
                    this.#datalayerService.pushData(params);
                } catch (error) {
                    console.error('[PageviewHandler] Failed to push pageview data:', error);
                }

                // Merge params - spätere Elemente überschreiben frühere bei Konflikten
                Object.assign(elementParams, params);
            }
        });

        // Kombiniere Basedata mit Element-Parametern
        // Element-Parameter überschreiben Basedata bei Konflikten
        const combinedParams = {
            ...basedata,
            ...elementParams
        };

        // Prüfe auf A/B-Test Parameter und erstelle ggf. experience_impression Event
        this.#checkForExperiment(combinedParams);

        // Pushe das kombinierte page_view Event
        try {
            this.#datalayerService.pushEvent('page_view', combinedParams);
        } catch (error) {
            console.error('[PageviewHandler] Failed to push page_view event:', error);
        }
    }

    /**
     * Sammelt Basedata für das page_view Event
     * @private
     * @returns {Object} Basedata-Objekt
     */
    #getBasedata() {
        const mandantid = getMandantId();
        const basedata = {
            "base-mandant": getBaseMandantId(mandantid),
            "base-name": getBaseMandantName(mandantid),
            "mandant-id": mandantid,
            "mandant-name": getBaseMandantName(mandantid),
            "device-size": getDeviceSize(),
            "viewport-width": getViewportWidth(),
            "stage": getStage(document.location.hostname || ""),
            "seite": window.location.pathname,
            "color_scheme": getColorScheme(),
            "kundennummer": getKundennummer()
        };

        const referrer = getReferrer();
        if (referrer) {
            basedata["referrer"] = referrer;
        }

        return basedata;
    }

    /**
     * Prüft auf A/B-Test Parameter und erstellt ggf. ein experience_impression Event
     * Unterstützt sowohl "ab-test" als auch "ab_test" Parameter
     * @private
     * @param {Object} params - Event-Parameter (z.B. von page_view)
     */
    #checkForExperiment(params) {
        try {
            // Prüfe auf ab-test oder ab_test Parameter
            const testString = params['ab-test'] || params['ab_test'];
            if (!testString) {
                return;
            }

            // Zuerst beim letzten Unterstrich splitten
            let lastSeparatorIndex = testString.lastIndexOf('_');

            // Fallback: Wenn kein Unterstrich vorhanden, beim letzten Bindestrich splitten
            if (lastSeparatorIndex === -1) {
                lastSeparatorIndex = testString.lastIndexOf('-');
            }

            // Wenn weder Unterstrich noch Bindestrich vorhanden, kein Event erstellen
            if (lastSeparatorIndex === -1) {
                return;
            }

            // Extrahiere Test und Variante
            const test = testString.substring(0, lastSeparatorIndex).split('-').join('_');
            const variante = testString.substring(lastSeparatorIndex + 1).split('-').join('_');
            const expVariantString = `THALIA-${test}-${variante}`;

            // Erstelle dl-event Element für experience_impression
            const htmlString = `<dl-event name="experience_impression" exp_variant_string="${expVariantString}" test="${test}" variant="${variante}" experiment-id="${test}" variant-id="${variante}"></dl-event>`;
            document.body.insertAdjacentHTML('beforeend', htmlString);
        } catch (error) {
            // Fehler stillschweigend ignorieren (wie im Original)
        }
    }
}
